/**
 * src/components/DriverRoleRoute.tsx
 *
 * Route guard for driver-only pages.
 *
 * WP-ATTICDEV-032-DRIVER (Task 1 -- Story 5):
 *   AC-006: Nested inside <ProtectedRoute> so unauthenticated visits redirect to /login.
 *   AC-008: Redirects authenticated users whose role resolves to customer (or null/
 *           undefined) to /dashboard; only driver and both may pass through.
 *
 * Three-state handling:
 *   loading === true  => render <LoadingFallback> to avoid a premature redirect when
 *                        the profile fetch is still in flight (deep-link safety).
 *   role === driver | both => render <Outlet> (allow access).
 *   role === customer | null => redirect to /dashboard (AC-008).
 */

import { useRef } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { useCustomerRole } from '../hooks/useCustomerRole';
import { LoadingFallback } from './LoadingFallback';

export function DriverRoleRoute() {
  const { role, loading } = useCustomerRole();

  // While the Customer profile fetch is in flight, hold on the loading screen
  // rather than immediately redirecting -- a driver deep-linking to /driver/jobs
  // should NOT be bounced to /dashboard just because the fetch has not resolved yet.
  //
  // BUT only on the INITIAL resolution: useCustomerRole (=useCustomerProfile)
  // re-enters loading on every background refetch (roleVersion bumps, token
  // settle after sign-in). Returning LoadingFallback on those would unmount +
  // remount the whole driver subtree on each flap. The profile keeps its prior
  // value during a refetch, so `role` stays valid — keep the subtree mounted.
  // (Mirrors CustomerRoleRoute; that flap stalled the gated onboarding flow.)
  const resolvedOnceRef = useRef(false);
  if (!loading) resolvedOnceRef.current = true;
  if (loading && !resolvedOnceRef.current) {
    return <LoadingFallback />;
  }

  // driver, both, or admin => allow through.
  if (role === "driver" || role === "both" || role === "admin") {
    return <Outlet />;
  }

  // customer, null, or any other falsy value => redirect (AC-008).
  return <Navigate to="/dashboard" replace />;
}
